import type { ObjectDirective } from 'vue'
import { loadGoogleMapsApi } from '@/utils/maps'

/**
 * Reusable Google Places address autocomplete directive.
 *
 * US ONLY: Predictions are restricted to United States addresses
 * (componentRestrictions.country = 'us'). This is hard-coded by design
 * because the application only operates in the US.
 *
 * Usage in any component:
 *
 *   import { vAddressAutocomplete, type GoogleAddressFields } from '@/directives/addressAutocomplete'
 *
 *   // In <script setup> the local name `vAddressAutocomplete` is auto-registered as `v-address-autocomplete`.
 *   // In Options API, register via `directives: { 'address-autocomplete': vAddressAutocomplete }`.
 *
 *   <input
 *     v-model="form.street"
 *     v-address-autocomplete="(fields: GoogleAddressFields) => onPlace(fields)"
 *   />
 *
 * Or pass an options object if you need access to the raw place result:
 *
 *   <input v-address-autocomplete="{ onSelect: (fields, place) => ... }" />
 *
 * The directive reads the API key from one of:
 *   - import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY
 *   - import.meta.env.VITE_GOOGLE_MAPS_API_KEY
 */

export interface GoogleAddressFields {
  street: string
  unit: string
  city: string
  county: string
  state: string
  zip: string
  country: string
}

export interface AddressAutocompleteOptions {
  onSelect: (fields: GoogleAddressFields, place: GooglePlaceLike) => void
}

export type AddressAutocompleteBinding =
  | ((fields: GoogleAddressFields) => void)
  | AddressAutocompleteOptions

interface GoogleAddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

export interface GooglePlaceLike {
  address_components?: GoogleAddressComponent[]
  formatted_address?: string
}

// US-only configuration. Do not parameterize: the application is US-based.
const AUTOCOMPLETE_TYPES = ['address']
const AUTOCOMPLETE_COUNTRY = 'us'
const AUTOCOMPLETE_FIELDS = ['address_components', 'formatted_address']

const GOOGLE_MAPS_API_KEY =
  (import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY as string | undefined) ||
  (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ||
  ''

let googleMapsLoadPromise: Promise<void> | null = null
let pacStyleInjected = false

const injectPacContainerStyle = () => {
  if (pacStyleInjected || typeof document === 'undefined') return
  pacStyleInjected = true
  const style = document.createElement('style')
  style.setAttribute('data-address-autocomplete', 'true')
  // Themed Google Places dropdown that follows the app's CSS variables
  // (light + dark mode via --surface-card / --surface-border / --text-color /
  // --surface-hover / --ameritrust-primary).
  style.textContent = `
    .pac-container {
      z-index: 10000 !important;
      margin-top: 6px;
      padding: 4px;
      /* Override Google's inline width so the full address is readable
         regardless of how narrow the underlying input column is. */
      min-width: 360px !important;
      width: max-content !important;
      max-width: min(560px, calc(100vw - 32px)) !important;
      background-color: var(--surface-card, #ffffff);
      border: 1px solid var(--surface-border, #e5e7eb);
      border-radius: 10px;
      box-shadow:
        0 10px 25px -8px rgba(0, 0, 0, 0.18),
        0 4px 10px -4px rgba(0, 0, 0, 0.08);
      font-family: inherit;
      overflow: hidden;
    }
    .pac-container::after {
      padding: 6px 10px 4px;
      opacity: 0.75;
    }
    .pac-item {
      padding: 9px 10px;
      border-top: none;
      border-radius: 6px;
      cursor: pointer;
      color: var(--text-color, #1f2937);
      font-size: 0.875rem;
      line-height: 1.25rem;
      display: flex;
      align-items: flex-start;
      gap: 2px;
      /* Wrap long addresses instead of truncating with an ellipsis. */
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      transition: background-color 0.12s ease;
    }
    .pac-item + .pac-item {
      margin-top: 2px;
    }
    .pac-item:hover,
    .pac-item-selected,
    .pac-item-selected:hover {
      background-color: var(--surface-hover, #f3f4f6);
    }
    .pac-icon {
      opacity: 0.7;
      margin-right: 8px;
      flex-shrink: 0;
      /* Align icon with the first text line on wrapped items. */
      margin-top: 2px;
    }
    .pac-item-query {
      font-size: 0.875rem;
      color: var(--text-color, #1f2937);
      padding-right: 4px;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
    }
    .pac-matched {
      font-weight: 600;
      color: var(--ameritrust-primary, #3D1951);
    }
  `
  document.head.appendChild(style)
}

const ensureGoogleMapsLoaded = (): Promise<void> => {
  if (!GOOGLE_MAPS_API_KEY) {
    console.warn(
      '[addressAutocomplete] Google Maps API key missing. Set VITE_APP_GOOGLE_MAPS_API_KEY or VITE_GOOGLE_MAPS_API_KEY.',
    )
    return Promise.resolve()
  }
  if (!googleMapsLoadPromise) {
    googleMapsLoadPromise = loadGoogleMapsApi(GOOGLE_MAPS_API_KEY).catch((err) => {
      googleMapsLoadPromise = null
      throw err
    })
  }
  return googleMapsLoadPromise
}

export const parseGoogleAddressComponents = (place: GooglePlaceLike): GoogleAddressFields => {
  const fields: GoogleAddressFields = {
    street: '',
    unit: '',
    city: '',
    county: '',
    state: '',
    zip: '',
    country: '',
  }
  let streetNumber = ''
  let streetName = ''

  place.address_components?.forEach((component: GoogleAddressComponent) => {
    const type = component.types[0]
    switch (type) {
      case 'street_number':
        streetNumber = component.long_name
        break
      case 'route':
        streetName = component.long_name
        break
      case 'subpremise':
        fields.unit = component.long_name
        break
      case 'locality':
        fields.city = component.long_name
        break
      case 'sublocality':
      case 'sublocality_level_1':
      case 'postal_town':
        if (!fields.city) fields.city = component.long_name
        break
      case 'administrative_area_level_2':
        // Google returns county as "Los Angeles County"; the app enums
        // store the bare county name ("Los Angeles"), so strip the suffix.
        fields.county = component.long_name.replace(/\s+County$/i, '').trim()
        break
      case 'administrative_area_level_1':
        fields.state = component.short_name
        break
      case 'postal_code':
        fields.zip = component.long_name
        break
      case 'country':
        fields.country = component.long_name
        break
    }
  })

  fields.street = `${streetNumber} ${streetName}`.trim()
  return fields
}

const resolveOptions = (
  binding: AddressAutocompleteBinding | undefined,
): AddressAutocompleteOptions | null => {
  if (!binding) return null
  if (typeof binding === 'function') return { onSelect: binding }
  if (typeof binding === 'object' && typeof binding.onSelect === 'function') return binding
  return null
}

interface AttachedState {
  autocomplete: any
  options: AddressAutocompleteOptions
  focusHandler: ((ev: FocusEvent) => void) | null
}

const ATTACHED_KEY = '__addressAutocompleteState'

const attachAutocomplete = (el: HTMLInputElement) => {
  const state = (el as any)[ATTACHED_KEY] as AttachedState | undefined
  if (!state || state.autocomplete) return
  const maps = (window as any).google?.maps
  if (!maps?.places?.Autocomplete) return

  const autocomplete = new maps.places.Autocomplete(el, {
    types: AUTOCOMPLETE_TYPES,
    componentRestrictions: { country: AUTOCOMPLETE_COUNTRY },
    fields: AUTOCOMPLETE_FIELDS,
  })
  state.autocomplete = autocomplete

  autocomplete.addListener('place_changed', () => {
    const place = autocomplete.getPlace() as GooglePlaceLike
    if (!place || !place.address_components) return
    const parsed = parseGoogleAddressComponents(place)
    const current = (el as any)[ATTACHED_KEY] as AttachedState | undefined
    current?.options.onSelect(parsed, place)
  })
}

export const vAddressAutocomplete: ObjectDirective<HTMLInputElement, AddressAutocompleteBinding> = {
  mounted(el, binding) {
    const options = resolveOptions(binding.value)
    if (!options) return
    ;(el as any)[ATTACHED_KEY] = {
      autocomplete: null,
      options,
      focusHandler: null,
    } as AttachedState
    injectPacContainerStyle()

    // Fallback: if the user focuses the input before Google Maps finished
    // loading (or before mount completed), try to attach on focus too.
    const focusHandler = () => attachAutocomplete(el)
    ;(el as any)[ATTACHED_KEY].focusHandler = focusHandler
    el.addEventListener('focus', focusHandler)

    ensureGoogleMapsLoaded()
      .then(() => attachAutocomplete(el))
      .catch((err) => console.error('[addressAutocomplete] Failed to initialize:', err))
  },
  updated(el, binding) {
    const options = resolveOptions(binding.value)
    const state = (el as any)[ATTACHED_KEY] as AttachedState | undefined
    if (!state || !options) return
    state.options = options
    // If autocomplete is somehow missing (e.g., element was reused without
    // re-running mounted), reattach now that the directive is updated.
    if (!state.autocomplete) attachAutocomplete(el)
  },
  unmounted(el) {
    const state = (el as any)[ATTACHED_KEY] as AttachedState | undefined
    if (state?.focusHandler) {
      el.removeEventListener('focus', state.focusHandler)
    }
    const maps = (window as any).google?.maps
    if (state?.autocomplete && maps?.event) {
      maps.event.clearInstanceListeners(state.autocomplete)
    }
    delete (el as any)[ATTACHED_KEY]
  },
}

export default vAddressAutocomplete
